<template>
    <div class="wbk-map__container">
        <div id="wbk-map" class="wbk-map">
        </div>
        <div class="wbk-map__overlay">
            <p>Anzahl der Projekte:</p>
            <ul :class="['wbk-map__legend', legendClass]" v-if="colorPalette[activeLayout]">
                <li><span :style="`background: ${colorPalette[activeLayout][0]}`"></span>keine</li>
                <li><span :style="`background: ${colorPalette[activeLayout][1]}`"></span>1 bis 5</li>
                <li><span :style="`background: ${colorPalette[activeLayout][2]}`"></span>> 5</li>
                <li><span :style="`background: ${colorPalette[activeLayout][3]}`"></span>> 10</li>
            </ul>
        </div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import mapLayerStyles from '@/service/mapLayerStyles'
import _find from 'lodash/find'
import _filter from 'lodash/filter'

import { mapGetters, mapState } from 'vuex'

export default {
    name: 'WBKMap',
    data() {
        return {
            map: null,
            hoveredStateId: null,
            activeLayout: 'landkreise-default',
            colorPalette: {},
            legendClass: 'wbk-map__legend--default',
            actionFieldNames: {
                infrastructure: 'Freiraum und Grüne Infrastruktur',
                mobility: 'Verkehr und Mobilität',
                economy: 'Wirtschaft und Gewerbe',
                housing: 'Wohnen und Soziales'
            }
        }
    },
    computed: {
        ...mapState( [
            'regionFilterset'
        ] ),
        ...mapGetters( [
            'projectsData',
            'regionsData',
            'actionFieldData',
            'actionFieldFilter',
            'projectsByRegion',
            'actionFieldByName',
            'regionById',
            'mainSetup'
        ] )
    },
    methods: {
        mapMouseOver( event ) {
            if ( event.features.length > 0 ) {
                const description = `
                    <div class="wbk-map__popup">
                        <h3>${event.features[0].properties.GEN} (${event.features[0].properties.BEZ})</h3>
                        <h4>${event.features[0].properties.projectsCount} ${event.features[0].properties.projectsCount === 1 ? 'Projekt' : 'Projekte'}</h4>
                        <h5 class="wbk-map__popup-headline--infrastructure">${this.actionFieldNames.infrastructure} (${event.features[0].properties.projectsInfrastructure})</h5>
                        <h5 class="wbk-map__popup-headline--mobility">${this.actionFieldNames.mobility} (${event.features[0].properties.projectsMobility})</h5>
                        <h5 class="wbk-map__popup-headline--economy">${this.actionFieldNames.economy} (${event.features[0].properties.projectsEconomy})</h5>
                        <h5 class="wbk-map__popup-headline--housing">${this.actionFieldNames.housing} (${event.features[0].properties.projectsHousing})</h5>
                    </div>
                `

                this.popup.setLngLat( event.lngLat ).setHTML( description ).addTo( this.map )

                if ( this.hoveredStateId !== null ) {
                    this.map.setFeatureState(
                        { source: 'landkreise', id: this.hoveredStateId },
                        { hover: false }
                    )
                }
                this.hoveredStateId = event.features[0].id;
                this.map.setFeatureState(
                    { source: 'landkreise', id: this.hoveredStateId },
                    { hover: true }
                )

            }
        },
        mapMouseOut( event ) {
            if ( this.hoveredStateId !== null ) {
                this.map.setFeatureState(
                    { source: 'landkreise', id: this.hoveredStateId },
                    { hover: false }
                )
            }
            this.hoveredStateId = null
            this.popup.remove()
        },
        mapClick( event ) {
            if ( event.features.length > 0 ) {
                this.$store.dispatch( 'updateRegionsFilter', event.features[0].properties.regionId )
                this.$emit( 'selectRegion', this.regionById( event.features[0].properties.regionId ) )
            }
        },
        setLayout( layoutId ) {
            this.map.setLayoutProperty( this.activeLayout, 'visibility', 'none' )
            this.map.setLayoutProperty( layoutId, 'visibility', 'visible' )
            this.activeLayout = layoutId
        },
        setColorPalette( styleId ) {
            const setColor = ( colorValue, fieldId, projectCountId ) => {
                this.colorPalette[`landkreise-${fieldId}`] = []

                for ( let i = 0; i <= 3; i++ ) {
                    this.colorPalette[`landkreise-${fieldId}`][i] = colorValue[`color_${i}`]
                }

                return [
                    'case',
                    ['==', ['get', projectCountId], 0 ], this.colorPalette[`landkreise-${fieldId}`][0],
                    ['>', ['get', projectCountId], 10 ], this.colorPalette[`landkreise-${fieldId}`][3],
                    ['>', ['get', projectCountId], 5 ], this.colorPalette[`landkreise-${fieldId}`][2],
                    ['<=', ['get', projectCountId], 5 ], this.colorPalette[`landkreise-${fieldId}`][1],
                    'black'
                ]
            }

            switch ( styleId ) {
                case 'landkreise-infrastructure':
                    return setColor( this.actionFieldByName( this.actionFieldNames.infrastructure ).colors, 'infrastructure', 'projectsInfrastructure' )
                case 'landkreise-mobility':
                    return setColor( this.actionFieldByName( this.actionFieldNames.mobility ).colors, 'mobility', 'projectsMobility' )
                case 'landkreise-economy':
                    return setColor( this.actionFieldByName( this.actionFieldNames.economy ).colors, 'economy', 'projectsEconomy' )
                case 'landkreise-housing':
                    return setColor( this.actionFieldByName( this.actionFieldNames.housing ).colors, 'housing', 'projectsHousing' )
                default:
                    return setColor( this.mainSetup.colors, 'default', 'projectsCount' )
            }
        },
        mapLoadSetup() {
            this.$store.state.geoData.features.forEach( ( feature, index ) => {
                const matchedRegion = _find( this.regionsData, region => region.name === `${feature.properties.GEN} (${feature.properties.BEZ})` )

                feature.properties.regionId = matchedRegion ? matchedRegion.id : null
                feature.properties.projectsCount = matchedRegion ? matchedRegion.count : 0

                const projectsByRegion = matchedRegion ? this.projectsByRegion( matchedRegion.id ) : null

                feature.properties.projectsInfrastructure = matchedRegion ? matchedRegion['count_freiraum-und-gruene-infrastruktur'] : 0
                feature.properties.projectsMobility = matchedRegion ? matchedRegion['count_verkehr-und-mobilitaet'] : 0
                feature.properties.projectsEconomy = matchedRegion ? matchedRegion['count_wirtschaft-und-gewerbe'] : 0
                feature.properties.projectsHousing = matchedRegion ? matchedRegion['count_wohnen-und-soziales'] : 0
                feature.id = index
            } )

            this.map.addSource( 'landkreise', {
                type: 'geojson',
                data: this.$store.state.geoData
            } )

            mapLayerStyles.forEach( ( style ) => {
                this.map.addLayer( style )

                if ( style.id !== 'landkreise-mouse' && style.id !== 'landkreise-line' ) {
                    if ( style.id !== 'landkreise-default' ) this.map.setLayoutProperty( style.id, 'visibility', 'none' )

                    this.map.setPaintProperty( style.id, 'fill-color', this.setColorPalette( style.id ) )
                }
            } )
        },
        initMap() {
            mapboxgl.accessToken = this.mainSetup.mapApiKey

            const mapControls = new mapboxgl.NavigationControl( {
                showCompass: false
            } )
            // const bounds = [
            //     [50.32362888905891,14.589969239278958],
            //     [45.97453801421747,8.13994504874725]
            // ]
            this.popup = new mapboxgl.Popup( {
                closeButton: false,
                closeOnClick: false,
                offset: 30
            } )
            this.map = new mapboxgl.Map( {
                attributionControl: false,
                container: 'wbk-map',
                style: this.mainSetup.mapStyleUrl,
                center: [11.549776682008655,48.16439757365595],
                zoom: 7,
                minZoom: 6.249628086862355,
                maxZoom: 8.660535293591948,
                // maxBounds: bounds,
                scrollZoom: false,
                touchZoomRotate: false
            } )
            this.map.addControl( mapControls, 'top-left' )
            this.map.on( 'load', this.mapLoadSetup )
            this.map.on( 'mousemove', 'landkreise-mouse', this.mapMouseOver )
            this.map.on( 'mouseleave', 'landkreise-mouse', this.mapMouseOut )
            this.map.on( 'click', 'landkreise-mouse', this.mapClick )
            this.map.on( 'drag', ( event ) => console.log( event ) )
        }
    },
    watch: {
        actionFieldFilter( filterValue ) {
            switch ( filterValue[0].name ) {
                case this.actionFieldNames.infrastructure:
                    this.setLayout( 'landkreise-infrastructure' )
                    this.legendClass = 'wbk-map__legend--infrastructure'
                    break
                case this.actionFieldNames.mobility:
                    this.setLayout( 'landkreise-mobility' )
                    this.legendClass = 'wbk-map__legend--mobility'
                    break
                case this.actionFieldNames.economy:
                    this.setLayout( 'landkreise-economy' )
                    this.legendClass = 'wbk-map__legend--economy'
                    break
                case this.actionFieldNames.housing:
                    this.setLayout( 'landkreise-housing' )
                    this.legendClass = 'wbk-map__legend--housing'
                    break
                default:
                    this.setLayout( 'landkreise-default' )
                    this.legendClass = 'wbk-map__legend--default'
                    break
            }
        }
    },
    async mounted() {
        this.$store.dispatch( 'fetchGeoData' ).then( () => this.initMap() )
    }
}
</script>

<style lang="scss">
    @import "~mapbox-gl/dist/mapbox-gl.css";

    .wbk-map {
        margin-top: 100px;
        height: 600px;
        width: 100%;

        &__container {
            position: relative;
        }

        &__overlay {
            position: absolute;
            width: 60%;
            bottom: -60px;
            left: 0;
            margin: 0 auto;
        }

        &__legend {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: space-between;

            li {
                display: flex;
                align-items: center;

                span {
                    content: "";
                    display: block;
                    width: 15px;
                    height: 15px;
                    margin-right: 10px;
                    border-radius: 50%;
                    background: black;
                }
            }
        }

        .mapboxgl-ctrl-bottom-left {
            left: initial;
            right: 0;
        }
    }
</style>
